import { useEffect, useRef, useState, type ReactNode } from "react";
import styled from "@emotion/styled";
import { theme } from "../lib/theme";

/**
 * Defers mounting a chart until it is near the viewport.
 *
 * Recharts builds a sizeable SVG tree per chart, and the Blocks page stacks four of them — all
 * mounted eagerly on load even though at most one is visible on a phone. Gating on an
 * IntersectionObserver keeps the initial render to what's actually on screen without changing
 * the layout: the placeholder reserves the chart's height, so nothing shifts when it swaps in.
 *
 * Once mounted, it stays mounted — remounting on scroll-out would throw away Recharts' internal
 * state and make scrolling back up flicker.
 */
const Placeholder = styled.div<{ h: number }>`
  height: ${(p) => p.h}px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.xs};
`;

export interface LazyChartProps {
  /** Reserved height, matching the chart's own so the swap causes no layout shift. */
  height: number;
  /** How far outside the viewport to start mounting. */
  rootMargin?: string;
  children: ReactNode;
}

export function LazyChart({ height, rootMargin = "200px", children }: LazyChartProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    // No IntersectionObserver (old Safari, jsdom) — render immediately rather than never.
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setVisible(true);
      },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, rootMargin]);

  return (
    <div ref={ref}>
      {visible ? children : <Placeholder h={height}>Loading chart…</Placeholder>}
    </div>
  );
}
